import { useMemo } from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { DataTable, DataTableSortHeader } from '@/components/ui/data-table';
import { PlatformLogo } from '@/components/connectors/library/PlatformLogo';
import { Settings, Play } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import type { ConnectorHealthInfo } from '@/hooks/useConnectorStats';
import { formatDuration } from '@/utils/formatDuration';

// STATUS_PILL mirrors ConnectorTable.tsx — OKLCH semantic tokens only, no raw palette levels.
// Note: health_status union is 'healthy'|'warning'|'error'|'disabled'|'unknown'.
// 'running' is not in the union; omitted to satisfy TypeScript strict mode.
const STATUS_PILL: Record<ConnectorHealthInfo['health_status'], { className: string; label: string }> = {
  healthy:  { className: 'bg-success/10 text-success border-success/30', label: 'Healthy' },
  warning:  { className: 'bg-warning/10 text-warning border-warning/30', label: 'Warning' },
  error:    { className: 'bg-destructive/10 text-destructive border-destructive/30', label: 'Error' },
  disabled: { className: 'bg-muted text-muted-foreground', label: 'Disabled' },
  unknown:  { className: 'bg-muted text-muted-foreground', label: 'Unknown' },
};

interface ConnectorHealthTableProps {
  connectors: ConnectorHealthInfo[];
  isLoading: boolean;
  onConfigure?: (connectorType: string) => void;
  onRunNow?: (connectorType: string) => void;
}

export function ConnectorHealthTable({
  connectors,
  isLoading,
  onConfigure,
  onRunNow,
}: ConnectorHealthTableProps) {
  const columns = useMemo<ColumnDef<ConnectorHealthInfo>[]>(
    () => [
      {
        accessorKey: 'name',
        header: ({ column }) => (
          <DataTableSortHeader column={column}>Connector</DataTableSortHeader>
        ),
        cell: ({ row }) => {
          const c = row.original;
          return (
            <div className="flex items-center gap-3">
              <PlatformLogo connectorType={c.connector_type} size={22} />
              <div className="min-w-0">
                <div className="font-medium truncate">{c.name}</div>
                <div className="text-xs text-muted-foreground font-mono">{c.connector_type}</div>
              </div>
            </div>
          );
        },
      },
      {
        accessorKey: 'health_status',
        header: ({ column }) => (
          <DataTableSortHeader column={column}>Status</DataTableSortHeader>
        ),
        cell: ({ row }) => {
          const p = STATUS_PILL[row.original.health_status] ?? STATUS_PILL.unknown;
          return (
            <Badge variant="outline" className={`${p.className} text-xs whitespace-nowrap`}>
              <span aria-hidden="true">● </span>
              <span>{p.label}</span>
            </Badge>
          );
        },
      },
      {
        accessorKey: 'recent_success_rate',
        header: ({ column }) => (
          <DataTableSortHeader column={column}>Success rate</DataTableSortHeader>
        ),
        cell: ({ row }) => {
          const r = row.original.recent_success_rate;
          if (r == null) return <span className="text-muted-foreground">—</span>;
          const color = r >= 95 ? 'bg-success' : r >= 80 ? 'bg-warning' : 'bg-destructive';
          const txt = r >= 95 ? 'text-success' : r >= 80 ? 'text-warning' : 'text-destructive';
          return (
            <div className="flex items-center justify-end gap-2">
              <span className="h-1.5 w-[52px] rounded-full bg-muted overflow-hidden">
                <span
                  className={`block h-full rounded-full ${color}`}
                  style={{ width: `${Math.min(100, r)}%` }}
                />
              </span>
              <span className={`tabular-nums font-medium ${txt}`}>{r.toFixed(1)}%</span>
            </div>
          );
        },
      },
      {
        accessorKey: 'executions_period',
        header: ({ column }) => (
          <DataTableSortHeader column={column}>Runs</DataTableSortHeader>
        ),
        cell: ({ row }) => (
          <span className="tabular-nums">
            {row.original.executions_period.toLocaleString('en-US')}
          </span>
        ),
      },
      {
        accessorKey: 'records_processed',
        header: ({ column }) => (
          <DataTableSortHeader column={column}>Records</DataTableSortHeader>
        ),
        cell: ({ row }) => (
          <span className="tabular-nums">
            {row.original.records_processed.toLocaleString('en-US')}
          </span>
        ),
      },
      {
        accessorKey: 'avg_duration_seconds',
        header: ({ column }) => (
          <DataTableSortHeader column={column}>Avg duration</DataTableSortHeader>
        ),
        cell: ({ row }) => {
          const d = row.original.avg_duration_seconds;
          return d == null
            ? <span className="text-muted-foreground">—</span>
            : <span className="tabular-nums">{formatDuration(d)}</span>;
        },
      },
      {
        accessorKey: 'last_execution_at',
        header: ({ column }) => (
          <DataTableSortHeader column={column}>Last run</DataTableSortHeader>
        ),
        cell: ({ row }) => {
          const t = row.original.last_execution_at;
          return (
            <span className="text-muted-foreground">
              {t ? formatDistanceToNow(new Date(t), { addSuffix: true }) : 'Never run'}
            </span>
          );
        },
      },
      {
        id: 'actions',
        enableSorting: false,
        header: () => <span className="sr-only">Actions</span>,
        cell: ({ row }) => {
          const c = row.original;
          return (
            <div className="flex items-center justify-end gap-1">
              {onConfigure && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onConfigure(c.connector_type)}
                  aria-label={`Configure ${c.name}`}
                >
                  <Settings className="h-4 w-4" />
                </Button>
              )}
              {onRunNow && c.enabled && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onRunNow(c.connector_type)}
                  aria-label={`Run ${c.name} now`}
                >
                  <Play className="h-4 w-4" />
                </Button>
              )}
            </div>
          );
        },
      },
    ],
    [onConfigure, onRunNow]
  );

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Connector Health</CardTitle>
          <CardDescription>Per-connector breakdown for the selected period.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-12 rounded-md bg-muted animate-pulse" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Connector Health</CardTitle>
        <CardDescription>Per-connector breakdown for the selected period.</CardDescription>
      </CardHeader>
      <CardContent>
        <DataTable
          data={connectors}
          columns={columns}
          getRowId={(c) => c.connector_config_id ?? `${c.connector_type}/${c.name}`}
          initialSorting={[{ id: 'name', desc: false }]}
          emptyState={
            <div className="text-center py-8 text-muted-foreground text-sm">
              No connectors configured
            </div>
          }
          hidePagination={connectors.length <= 20}
        />
      </CardContent>
    </Card>
  );
}
